<template>
  <b-card v-if="hasRewards" class="invite-card">
    <h3 class="invite-title">Invita y ganan los dos 🎁</h3>
    <p class="invite-sub">
      Cada colega que entre a Utopie con tu correo te suma
      <strong>{{ usesLabel(rewards.referring) }}</strong> — y a él/ella
      <strong>{{ usesLabel(rewards.referred) }}</strong>.
    </p>

    <ol class="invite-steps">
      <li>Aprieta el botón: WhatsApp se abre con el mensaje ya escrito.</li>
      <li>
        Tu colega entra con tu link y se identifica con Google.
        <strong>Un click, sin escribir nada.</strong>
      </li>
      <li>Se activan los usos para los dos, al instante.</li>
    </ol>

    <div class="invite-preview">
      <label class="invite-preview-label" for="invite-msg">
        Esto es lo que le llega — puedes editarlo
      </label>
      <textarea
        id="invite-msg"
        v-model="draft"
        class="invite-preview-msg"
        rows="6"
        @input="edited = true"
      ></textarea>
      <button type="button" class="invite-copy-btn" @click="copyMessage">
        Copiar mensaje
      </button>
    </div>

    <a
      class="invite-wa"
      :href="whatsappUrl"
      target="_blank"
      rel="noopener"
      @click="medirCompartir('whatsapp')"
    >
      💬 Invitar a un colega
    </a>

    <div class="invite-copy">
      <span class="invite-copy-mail">{{ inviteLink }}</span>
      <button type="button" class="invite-copy-btn" @click="copy">
        Copiar link
      </button>
    </div>

    <div class="invite-counter">
      Colegas que entraron con tu correo: <strong>{{ referredAmount }}</strong>
      <span v-if="rewards.each_five">
        · al llegar a 5 → <strong>{{ usesLabel(rewards.each_five) }}</strong> extra
      </span>
      <div class="invite-dots">
        <span
          v-for="n in 5"
          :key="n"
          :class="['invite-dot', { on: n <= cycleProgress }]"
          >{{ n }}</span
        >
      </div>
    </div>
  </b-card>
</template>

<script>
// "Invita a tus amigos" view for the service chatbots. The shared link points at
// the service's logged-out demo carrying `?invita=<token>` (the inviter's signed
// user id, minted by `check_referred`). InvitationBanner picks it up on the
// other side and refers automatically once the visitor logs in — the manual
// "¿Fuiste invitado?" email box stays as the fallback for a link that got lost.
import userService from "@/services/userService";
import { medirInvitacionCompartida } from "@/services/analytics";
import {
  hasReferralRewards,
  rememberReferralRewards,
} from "./referralRewards";

// Where an invite for each chatbot's service drops the visitor: its logged-out
// demo, so a not-yet-registered colleague sees the product, not a login wall.
// Para qué está usando Utopie quien invita, por servicio. Va en el mensaje de
// WhatsApp: sin esto el texto decía sólo "estoy usando Utopie", y quien lo
// recibía tenía que abrir un link con un token al final para enterarse de qué
// se trataba — que es exactamente la forma de un mensaje de phishing.
//
// Cada frase completa "Estoy usando Utopie para ___", así que van en infinitivo
// y en primera persona: lo que hace un colega, no lo que ofrece un folleto.
const SERVICE_PITCH = {
  Eureka:
    "aplicar Eureka, la prueba psicopedagógica estandarizada: se corrige sola y entrega el informe con las desviaciones estándar",
  Planificaciones:
    "hacer mis planificaciones de clase: elijo curso, asignatura y objetivo, y bajo la clase lista con sus diapositivas",
  "Pruebas Informales":
    "aplicar pruebas de especialidad, listas para corregir y sacar el informe",
  "Documentos PIE":
    "responder los documentos del PIE en línea, con sugerencias, y bajar el Word",
  "Corregir Test":
    "corregir automáticamente los test del Decreto 170 (WISC-V, TEPROSIF-R, Perfil Sensorial): pongo los puntajes y baja el informe en Word",
  "Materiales para la clase":
    "armar los materiales de la clase: elijo los objetivos y sale un PDF listo para imprimir",
};

export const DEMO_ROUTE = {
  Eureka: "eureka-demo",
  Planificaciones: "planification-demo",
  "Pruebas Informales": "materials-demo",
  "Documentos PIE": "individual-documents-demo",
  "Corregir Test": "test-correction-demo",
  "Materiales para la clase": "class-materials-demo",
};

export default {
  name: "InviteFriendsView",
  props: { context: { type: Object, required: false } },
  data() {
    return {
      referredAmount: 0,
      rewards: { referring: 0, referred: 0, each_five: 0 },
      inviteToken: "",
      // El mensaje tal como se va a mandar. Arranca en el texto armado y queda
      // en manos de quien invita: nadie escribe igual que una plantilla, y un
      // mensaje con voz propia es justo lo que lo separa de un reenvío masivo.
      draft: "",
      edited: false,
    };
  },
  created() {
    const service = this.context ? this.context.service : "";
    userService.checkReferred(service).then((data) => {
      this.referredAmount = data.referred_amount || 0;
      if (data.rewards) this.rewards = data.rewards;
      this.inviteToken = data.invite_token || "";
      // Publish what we learned so the chatbot menu can stop offering an
      // invite that grants nothing (see referralRewardsKnownEmpty).
      rememberReferralRewards(service, this.rewards);
    });
  },
  watch: {
    // `message` se termina de armar cuando llega `check_referred` (los montos y
    // el token), o sea despues de montar. Se copia al borrador hasta que la
    // persona escribe: desde ahi manda lo suyo y no se le pisa lo que tipeo.
    message: {
      immediate: true,
      handler(value) {
        if (!this.edited) this.draft = value;
      },
    },
  },
  computed: {
    // Nothing renders until the fetch lands: `rewards` starts at all-zeros, and
    // showing the card first and hiding it a moment later would flash the very
    // "0 usos" promise this guard exists to prevent.
    hasRewards() {
      return hasReferralRewards(this.rewards);
    },
    // Filled dots = referrals since the last "every 5" bonus (5 of 5 right
    // after earning one, not 0 of 5).
    cycleProgress() {
      const rest = this.referredAmount % 5;
      return rest === 0 && this.referredAmount ? 5 : rest;
    },
    // El que se copia con "Copiar link".
    inviteLink() {
      return this.enlaceDeInvitacion("enlace");
    },
    // El que va dentro del mensaje. Es el mismo enlace con otro `utm_medium`:
    // sin separarlos, todo lo compartido se vería como una sola vía y no se
    // podría saber cuál trae gente.
    enlaceWhatsapp() {
      return this.enlaceDeInvitacion("whatsapp");
    },
    // El mensaje que se manda por WhatsApp.
    //
    // Dice quién lo manda, qué es Utopie, y qué servicio le están compartiendo,
    // antes del link. Es lo que separa una invitación de un intento de phishing:
    // el mensaje anterior era una línea genérica ("estoy usando Utopie para el
    // trabajo del PIE") seguida de una URL con un token al final, o sea la forma
    // exacta de la estafa que a esta altura todo el mundo aprendió a no abrir.
    // El texto es editable (ver `draft`): quien invita le pone sus palabras,
    // que es lo que ningún envío masivo puede imitar.
    //
    // Un solo link en todo el mensaje, a propósito. Nombrar el dominio suelto
    // ("utopie.cl") ayudaba a verificar a dónde lleva, pero WhatsApp lo
    // autoenlaza: quedaban dos links, y la vista previa la arma con el primero
    // que encuentra. El invitado tocaba la portada del sitio en vez del
    // servicio al que lo invitaron, y ahí el `?invita=` no viaja — la
    // invitación se pierde sin que nadie se entere.
    //
    // Corto igual: cuatro líneas. En WhatsApp un texto largo se corta con un
    // "Leer más" y el link queda escondido detrás de ese pliegue.
    //
    // Los montos salen de RedpieConfig, nunca escritos a mano: este texto se va
    // a conversaciones que no controlamos, así que es la única promesa que no
    // podemos deshacer.
    message() {
      const forThem = Number(this.rewards.referred) || 0;
      const forMe = Number(this.rewards.referring) || 0;
      const gain = forThem
        ? `se activan ${this.usesLabel(forThem)} para ti${
            forMe ? ` y ${this.usesLabel(forMe)} para mí` : ""
          }`
        : `se activan ${this.usesLabel(forMe)} para mí`;
      const pitch =
        SERVICE_PITCH[this.context && this.context.service] ||
        "el trabajo del PIE";
      return (
        `Hola! 👋 Estoy usando Utopie para ${pitch}.\n\n` +
        `Te dejo mi link de invitación: entras con tu cuenta de Google, ` +
        `sin tarjeta, y ${gain} 🎁\n${this.enlaceWhatsapp}`
      );
    },
    // Lo que se manda es el borrador. Si al editarlo se borro el link, se
    // vuelve a pegar al final: sin el, la invitacion sale igual pero no
    // acredita a nadie, y eso no se nota hasta que los usos no llegan.
    outgoing() {
      const text = (this.draft || "").trim();
      if (!text) return this.message;
      // Contra el de WhatsApp, que es el que el mensaje trae: comparar con el
      // otro dejaria dos enlaces en el texto, que es justo lo que rompe la
      // vista previa de WhatsApp.
      return text.includes(this.enlaceWhatsapp)
        ? text
        : `${text}\n${this.enlaceWhatsapp}`;
    },
    whatsappUrl() {
      return `https://wa.me/?text=${encodeURIComponent(this.outgoing)}`;
    },
  },
  methods: {
    // El enlace de invitación, con su `utm` en la query REAL.
    //
    // POR QUÉ ANTES DEL `#`. Lo que va después del numeral no sale del
    // navegador: Django no lo ve nunca. El enlace era
    // `/utopie/#/<ruta>?invita=<token>`, o sea una URL sin una sola señal
    // legible para el servidor, así que cada visita traída por una invitación
    // aterrizaba como tráfico directo (o como "whatsapp.com" si el referrer
    // sobrevivía). Con los `utm_*` acá, `OrigenMiddleware` los levanta solo y
    // `OrigenCuenta` guarda `utm_source=referido` sin que haya que tocar el
    // servidor. GA4 atribuye la sesión por lo mismo.
    //
    // El token NO viaja en un `utm_*`: es un id de usuario firmado y no tiene
    // nada que hacer en un informe de campañas. Se queda donde estaba, después
    // del `#`, que es donde lo lee InvitationBanner.
    //
    // Sin token todavía (`check_referred` en vuelo) el enlace sigue siendo una
    // página válida: sólo no acredita a nadie.
    enlaceDeInvitacion(medio) {
      const route = DEMO_ROUTE[this.context && this.context.service] || "";
      const utm = `utm_source=referido&utm_medium=${medio}&utm_campaign=invita`;
      const invita = this.inviteToken
        ? `?invita=${encodeURIComponent(this.inviteToken)}`
        : "";
      return `${window.location.origin}/utopie/?${utm}#/${route}${invita}`;
    },
    // `share` es el evento de fábrica de GA4 para esto. Sale al apretar, no al
    // confirmar: el portapapeles no avisa si la persona llegó a pegarlo, y
    // WhatsApp se abre en otra pestaña.
    medirCompartir(metodo) {
      medirInvitacionCompartida(
        metodo,
        this.context && this.context.service
      );
    },
    usesLabel(amount) {
      const n = Number(amount) || 0;
      return `${n} ${n === 1 ? "uso" : "usos"}`;
    },
    // Sólo el link, para pegar en un correo o un chat.
    copy() {
      this.medirCompartir("copiar");
      this.copyText(this.inviteLink, "¡Link copiado! Pégalo donde quieras 😊");
    },
    // El mensaje completo, ya editado, para quien prefiere pegarlo a mano en
    // vez de pasar por el botón de WhatsApp.
    copyMessage() {
      this.medirCompartir("copiar");
      this.copyText(this.outgoing, "¡Mensaje copiado! Pégalo donde quieras 😊");
    },
    copyText(text, okMessage) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(
          () => this.$toasted.success(okMessage),
          () => this.$toasted.info(text)
        );
      } else {
        this.$toasted.info(text);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.invite-card {
  background-color: white !important;
  color: #000 !important;
  border: 2pt dashed var(--main-color, #208ac6) !important;
  border-radius: 15px !important;
  font-family: "Ubuntu" !important;
  text-align: left;
}
.invite-title {
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.invite-sub {
  margin-bottom: 1rem;
}
.invite-steps {
  padding-left: 1.2rem;
  margin-bottom: 1rem;
  li {
    margin-bottom: 0.35rem;
  }
}
.invite-preview {
  background: #f6f5f3;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}
.invite-preview-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #7a7a7a;
  margin-bottom: 0.35rem;
}
.invite-preview-msg {
  margin: 0 0 0.5rem;
  white-space: pre-wrap;
  display: block;
  width: 100%;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  padding: 0.5rem;
  background: #fff;
  font: inherit;
  color: inherit;
  resize: vertical;
}
.invite-wa {
  display: block;
  text-align: center;
  background: #25d366;
  color: white !important;
  font-weight: 700;
  padding: 0.75rem;
  border-radius: 999px;
  text-decoration: none;
  margin-bottom: 0.75rem;
}
.invite-copy {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  padding: 0.35rem 0.35rem 0.35rem 0.75rem;
  margin-bottom: 1rem;
}
.invite-copy-mail {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.invite-copy-btn {
  border: none;
  border-radius: 8px;
  background: var(--main-color, #208ac6);
  color: white;
  font-weight: 700;
  padding: 0.35rem 0.9rem;
}
.invite-counter {
  border-top: 1px solid #ebedec;
  padding-top: 0.75rem;
}
.invite-dots {
  display: flex;
  gap: 0.35rem;
  margin-top: 0.5rem;
}
.invite-dot {
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #aaa;
  &.on {
    background: var(--main-color, #208ac6);
    border-color: var(--main-color, #208ac6);
    color: white;
  }
}
</style>
